import * as React from "react";
import { AlertCircle, Loader2, Repeat } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import {
  EXPENSE_CATEGORY_LABELS,
  PAYMENT_METHOD_LABELS,
  RECURRING_CADENCE_LABELS,
  buildRecurringExpensePayload,
  paiseToRupees,
  rupeesToPaise,
  useCreateRecurringExpense,
  useSuggestExpense,
  useUpdateRecurringExpense,
  type ExpenseCategory,
  type PaymentMethod,
  type RecurringEndMode,
  type RecurringExpense,
  type RecurringExpenseCadence,
} from "@/lib/accounting/api";

interface DraftState {
  title: string;
  category: ExpenseCategory;
  customCategoryLabel: string;
  amountRupees: string;
  cadence: RecurringExpenseCadence;
  startsOn: string;
  endMode: RecurringEndMode;
  endsOn: string;
  occurrenceCount: string;
  effectiveFrom: string;
  vendorName: string;
  paymentMethod: PaymentMethod | "__none__";
  note: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function endModeOf(existing: RecurringExpense): RecurringEndMode {
  if (existing.occurrence_count !== null) return "count";
  if (existing.ends_on) return "until";
  return "never";
}

function emptyDraft(): DraftState {
  return {
    title: "",
    category: "electricity",
    customCategoryLabel: "",
    amountRupees: "",
    cadence: "monthly",
    startsOn: todayIso(),
    endMode: "never",
    endsOn: "",
    occurrenceCount: "6",
    effectiveFrom: todayIso(),
    vendorName: "",
    paymentMethod: "__none__",
    note: "",
  };
}

function draftFromExisting(existing: RecurringExpense | null | undefined): DraftState {
  if (!existing) return emptyDraft();
  return {
    title: existing.title,
    category: existing.category,
    customCategoryLabel: existing.custom_category_label ?? "",
    amountRupees: String(paiseToRupees(existing.amount_paise)),
    cadence: existing.cadence,
    startsOn: existing.starts_on.slice(0, 10),
    endMode: endModeOf(existing),
    endsOn: existing.ends_on?.slice(0, 10) ?? "",
    occurrenceCount: String(existing.occurrence_count ?? 6),
    effectiveFrom: todayIso(),
    vendorName: existing.vendor_name ?? "",
    paymentMethod: existing.payment_method ?? "__none__",
    note: existing.note ?? "",
  };
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export interface RecurringExpenseEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: RecurringExpense | null;
}

export function RecurringExpenseEditorSheet({
  open,
  onOpenChange,
  existing,
}: RecurringExpenseEditorSheetProps) {
  const isEdit = Boolean(existing);
  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const createMutation = useCreateRecurringExpense();
  const updateMutation = useUpdateRecurringExpense();
  const suggestMutation = useSuggestExpense();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setSubmitError(null);
    }
  }, [open, existing]);

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const amountPaise = rupeesToPaise(draft.amountRupees);
  const occurrenceCount = Number.parseInt(draft.occurrenceCount, 10);
  const customLabel = draft.category === "other" ? trimmedOrNull(draft.customCategoryLabel) : null;
  const endValid =
    draft.endMode === "never" ||
    (draft.endMode === "until" && draft.endsOn >= draft.startsOn && draft.endsOn.length > 0) ||
    (draft.endMode === "count" &&
      Number.isInteger(occurrenceCount) &&
      occurrenceCount >= 1 &&
      occurrenceCount <= 1200);
  const canSubmit =
    draft.title.trim().length > 0 &&
    amountPaise !== null &&
    draft.startsOn.length > 0 &&
    (draft.category !== "other" || customLabel !== null) &&
    endValid &&
    (!isEdit || draft.effectiveFrom.length > 0) &&
    !isSubmitting;

  const suggest = async () => {
    if (draft.title.trim().length === 0) return;
    setSubmitError(null);
    try {
      const result = await suggestMutation.mutateAsync({
        title: draft.title,
        note: draft.note,
        kind: "recurring",
      });
      setDraft((prev) => ({
        ...prev,
        category: result.category,
        customCategoryLabel: result.custom_category_label ?? "",
        cadence: result.cadence ?? prev.cadence,
      }));
      toast.success(result.rationale);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't suggest a category.";
      setSubmitError(message);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    if (!canSubmit || amountPaise === null) return;
    const payment = draft.paymentMethod === "__none__" ? null : draft.paymentMethod;
    const payload = buildRecurringExpensePayload({
      title: draft.title,
      category: draft.category,
      customCategoryLabel: draft.customCategoryLabel,
      amountPaise,
      cadence: draft.cadence,
      startsOn: draft.startsOn,
      endMode: draft.endMode,
      endsOn: draft.endsOn,
      occurrenceCount,
      vendorName: trimmedOrNull(draft.vendorName),
      paymentMethod: payment,
      note: trimmedOrNull(draft.note),
    });
    try {
      if (existing) {
        const saved = await updateMutation.mutateAsync({
          id: existing.id,
          body: { ...payload, effective_from: draft.effectiveFrom },
        });
        toast.success(`Updated ${saved.title}. Posted months stay as they were.`);
      } else {
        const saved = await createMutation.mutateAsync(payload);
        toast.success(`Added recurring cost ${saved.title}.`);
      }
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't save the recurring cost. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't save the recurring cost. Please retry.";
      setSubmitError(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="recurring-expense-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Repeat className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">
                {isEdit ? "Edit recurring cost" : "Add a recurring cost"}
              </SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                Water, electricity, rent, wages, or any other cost. It can run forever, until a
                date, or for a set number of bills.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Title" htmlFor="rec-title" required>
              <div className="flex gap-2">
                <Input
                  id="rec-title"
                  value={draft.title}
                  maxLength={200}
                  required
                  autoFocus
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Electricity"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={draft.title.trim().length === 0 || suggestMutation.isPending}
                  onClick={() => void suggest()}
                >
                  Suggest
                </Button>
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" htmlFor="rec-category" required>
                <Select
                  value={draft.category}
                  onValueChange={(value) => {
                    const category = value as ExpenseCategory;
                    setDraft((prev) => ({
                      ...prev,
                      category,
                      customCategoryLabel: category === "other" ? prev.customCategoryLabel : "",
                    }));
                  }}
                >
                  <SelectTrigger id="rec-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map((category) => (
                      <SelectItem key={category} value={category}>
                        {EXPENSE_CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Amount (₹)" htmlFor="rec-amount" required>
                <Input
                  id="rec-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={draft.amountRupees}
                  required
                  onChange={(e) => set("amountRupees", e.target.value)}
                  placeholder="4500"
                />
              </Field>
            </div>

            {draft.category === "other" ? (
              <Field label="What is this cost?" htmlFor="rec-custom" required>
                <Input
                  id="rec-custom"
                  value={draft.customCategoryLabel}
                  maxLength={80}
                  required
                  onChange={(e) => set("customCategoryLabel", e.target.value)}
                  placeholder="Generator diesel, tea, printing…"
                />
              </Field>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <Field label="How often" htmlFor="rec-cadence" required>
                <Select
                  value={draft.cadence}
                  onValueChange={(value) => set("cadence", value as RecurringExpenseCadence)}
                >
                  <SelectTrigger id="rec-cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(RECURRING_CADENCE_LABELS) as RecurringExpenseCadence[]).map(
                      (cadence) => (
                        <SelectItem key={cadence} value={cadence}>
                          {RECURRING_CADENCE_LABELS[cadence]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="First bill date" htmlFor="rec-start" required>
                <Input
                  id="rec-start"
                  type="date"
                  value={draft.startsOn}
                  required
                  onChange={(e) => set("startsOn", e.target.value)}
                />
              </Field>
            </div>

            <Field
              label="How long"
              htmlFor="rec-end-mode"
              helper="A 31st start date bills on the last day of shorter months."
            >
              <Select
                value={draft.endMode}
                onValueChange={(value) => set("endMode", value as RecurringEndMode)}
              >
                <SelectTrigger id="rec-end-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Keeps going</SelectItem>
                  <SelectItem value="until">Until a date</SelectItem>
                  <SelectItem value="count">For a set number of bills</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {draft.endMode === "until" ? (
              <Field label="Last bill on or before" htmlFor="rec-ends" required>
                <Input
                  id="rec-ends"
                  type="date"
                  min={draft.startsOn}
                  value={draft.endsOn}
                  required
                  onChange={(e) => set("endsOn", e.target.value)}
                />
              </Field>
            ) : null}

            {draft.endMode === "count" ? (
              <Field
                label="Number of bills"
                htmlFor="rec-count"
                required
                helper="Six monthly bills cover six months, including the first."
              >
                <Input
                  id="rec-count"
                  type="number"
                  min={1}
                  max={1200}
                  step={1}
                  value={draft.occurrenceCount}
                  required
                  onChange={(e) => set("occurrenceCount", e.target.value)}
                />
              </Field>
            ) : null}

            {isEdit ? (
              <Field
                label="Apply changes from"
                htmlFor="rec-effective"
                required
                helper="Months already posted stay on the books. Future bills use the new amount."
              >
                <Input
                  id="rec-effective"
                  type="date"
                  value={draft.effectiveFrom}
                  required
                  onChange={(e) => set("effectiveFrom", e.target.value)}
                />
              </Field>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Paid to" htmlFor="rec-vendor">
                <Input
                  id="rec-vendor"
                  value={draft.vendorName}
                  maxLength={200}
                  onChange={(e) => set("vendorName", e.target.value)}
                  placeholder="State Electricity Board"
                />
              </Field>
              <Field label="Payment method" htmlFor="rec-payment">
                <Select
                  value={draft.paymentMethod}
                  onValueChange={(value) =>
                    set("paymentMethod", value as DraftState["paymentMethod"])
                  }
                >
                  <SelectTrigger id="rec-payment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((method) => (
                      <SelectItem key={method} value={method}>
                        {PAYMENT_METHOD_LABELS[method]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Note" htmlFor="rec-note">
              <Textarea
                id="rec-note"
                value={draft.note}
                maxLength={2000}
                rows={2}
                onChange={(e) => set("note", e.target.value)}
                placeholder="Meter number, contract, or anything the next person should know."
              />
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save schedule" : "Add recurring cost"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  htmlFor,
  required,
  helper,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}
